import React, { useEffect, useState } from 'react';
import ChevronLeft from 'lucide-react/dist/esm/icons/chevron-left.js';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right.js';

/**
 * RichImageCarousel
 *
 * Full-parity slider for Classic / Modified 50/50 layouts. Accepts the richer
 * slide shape returned by `useHomeSlides` (image + optional title, subtitle,
 * CTA link) and falls back gracefully to an image-only display when fields are
 * absent. A plain `string[]` of image URLs is also accepted for backward
 * compatibility with existing call sites that pass `mainDraw.gallery_images`.
 *
 * Visual weight matches the original `ImageCarousel` so it drops into any
 * layout variant without surrounding restyles.
 */
export default function RichImageCarousel({
  slides,
  interval = 5000,
  className = '',
}) {
  const normalized = normalizeSlides(slides);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  // Respect prefers-reduced-motion — pause auto-rotate.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setPaused(mq.matches);
    apply();
    mq.addEventListener?.('change', apply);
    return () => mq.removeEventListener?.('change', apply);
  }, []);

  useEffect(() => {
    if (paused || normalized.length <= 1) return undefined;
    const id = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % normalized.length);
    }, interval);
    return () => clearInterval(id);
  }, [paused, normalized.length, interval]);

  // Clamp currentIndex if slides shrink.
  useEffect(() => {
    if (currentIndex >= normalized.length && normalized.length > 0) {
      setCurrentIndex(0);
    }
  }, [normalized.length, currentIndex]);

  if (normalized.length === 0) return null;

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? normalized.length - 1 : prev - 1));
  };
  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % normalized.length);
  };
  const goToSlide = (index) => setCurrentIndex(index);

  return (
    <div
      className={`relative w-full bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden ${className}`}
    >
      <div className="relative aspect-video">
        {normalized.map((slide, index) => {
          const isActive = index === currentIndex;
          // Only render current slide and adjacent slides to prevent
          // downloading all carousel images on page load
          const distance = Math.abs(index - currentIndex);
          const wrappedDistance = Math.min(distance, normalized.length - distance);
          const shouldRender = wrappedDistance <= 1;
          if (!shouldRender) return null;

          const hasOverlay = Boolean(slide.title || slide.subtitle || slide.linkUrl);
          return (
            <div
              key={`${slide.url}-${index}`}
              className={`absolute inset-0 transition-opacity duration-700 ${
                isActive ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
              aria-hidden={!isActive}
            >
              <img
                src={slide.url}
                alt={slide.title || `Slide ${index + 1}`}
                loading={index === 0 ? 'eager' : 'lazy'}
                className="w-full h-full object-cover"
              />

              {hasOverlay && (
                <div className="absolute inset-0 flex items-end">
                  <div className="w-full bg-gradient-to-t from-black/70 via-black/30 to-transparent p-4 sm:p-6 text-white">
                    {slide.title && (
                      <h3 className="text-xl sm:text-2xl font-bold drop-shadow-md">
                        {slide.title}
                      </h3>
                    )}
                    {slide.subtitle && (
                      <p className="mt-1 text-sm sm:text-base text-white/90 drop-shadow-md max-w-2xl">
                        {slide.subtitle}
                      </p>
                    )}
                    {slide.linkUrl && (
                      <a
                        href={slide.linkUrl}
                        target={isExternal(slide.linkUrl) ? '_blank' : undefined}
                        rel={isExternal(slide.linkUrl) ? 'noopener noreferrer' : undefined}
                        className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/90 hover:bg-white text-gray-900 font-semibold px-4 py-2 text-sm shadow-lg transition-colors"
                        tabIndex={isActive ? 0 : -1}
                      >
                        {slide.linkText || 'Learn more'}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {normalized.length > 1 && (
          <>
            <button
              type="button"
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-800 p-2 rounded-full shadow-lg transition-all hover:scale-110 z-10"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-5 h-5 text-gray-900 dark:text-white" />
            </button>
            <button
              type="button"
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-800 p-2 rounded-full shadow-lg transition-all hover:scale-110 z-10"
              aria-label="Next slide"
            >
              <ChevronRight className="w-5 h-5 text-gray-900 dark:text-white" />
            </button>
          </>
        )}
      </div>

      {normalized.length > 1 && (
        <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-10">
          {normalized.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full min-w-[24px] min-h-[24px] flex items-center justify-center ${
                index === currentIndex
                  ? 'bg-white w-8 h-3 shadow-lg'
                  : 'bg-white/60 w-3 h-3 hover:bg-white/90 hover:scale-125'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function normalizeSlides(input) {
  if (!Array.isArray(input)) return [];
  return input
    .map((item) => {
      if (!item) return null;
      if (typeof item === 'string') {
        return item.trim() ? { url: item } : null;
      }
      if (typeof item === 'object' && typeof item.url === 'string' && item.url.trim()) {
        return {
          url: item.url,
          title: item.title,
          subtitle: item.subtitle,
          linkUrl: item.linkUrl,
          linkText: item.linkText,
        };
      }
      return null;
    })
    .filter(Boolean);
}

function isExternal(url) {
  if (!url) return false;
  return /^(https?:)?\/\//i.test(url);
}
